import React, { useState, useEffect } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import './VisitsChart.css';

const formatHour = (hour) => {
  const hourNum = parseInt(hour);
  return `${String(hourNum).padStart(2, '0')}:00`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('fr-FR', { 
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
};

const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(year, month - 1);
  return date.toLocaleDateString('fr-FR', { 
    month: 'long',
    year: 'numeric'
  });
};

const CustomTooltip = ({ active, payload, label, chartType }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-date">
          {chartType === 'hourly' ? formatHour(label) :
           chartType === 'daily' ? formatDate(label) :
           formatMonth(label)}
        </p>
        <p className="tooltip-visits">
          <span className="tooltip-value">{payload[0].value}</span>
          <span className="tooltip-label">visites</span>
        </p>
      </div>
    );
  }
  return null;
};

// Ajouter une fonction utilitaire pour formater la date
const getFormattedDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Modifier la fonction calculateStats pour inclure les comparaisons
const calculateStats = (data, previousData) => {
  if (!data || data.length === 0) {
    return { 
      total: 0, 
      average: 0, 
      peak: 0,
      trends: { total: 0, average: 0, peak: 0 }
    };
  }

  // Calcul des stats actuelles
  const total = data.reduce((sum, item) => sum + item.visits, 0);
  const nonZeroHours = data.filter(item => item.visits > 0).length;
  const average = nonZeroHours > 0 ? Math.round(total / nonZeroHours) : 0;
  const peak = Math.max(...data.map(item => item.visits));

  // Calcul des stats précédentes
  const previousTotal = previousData?.reduce((sum, item) => sum + item.visits, 0) || 0;
  const previousNonZeroHours = previousData?.filter(item => item.visits > 0).length || 0;
  const previousAverage = previousNonZeroHours > 0 ? Math.round(previousTotal / previousNonZeroHours) : 0;
  const previousPeak = previousData ? Math.max(...previousData.map(item => item.visits)) : 0;

  // Calcul des tendances en pourcentage
  const calculateTrend = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const trends = {
    total: calculateTrend(total, previousTotal),
    average: calculateTrend(average, previousAverage),
    peak: calculateTrend(peak, previousPeak)
  };

  return { total, average, peak, trends };
};

const VisitsChart = () => {
  const [chartType, setChartType] = useState('hourly');
  const [dailyVisits, setDailyVisits] = useState([]);
  const [monthlyVisits, setMonthlyVisits] = useState([]);
  const [hourlyVisits, setHourlyVisits] = useState([]);
  const [previousHourlyVisits, setPreviousHourlyVisits] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    const formattedDate = getFormattedDate(today);
    return formattedDate;
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentStats, setCurrentStats] = useState({ total: 0, average: 0, peak: 0 });

  // Modifier la fonction changeDate pour utiliser la même fonction de formatage
  const changeDate = (direction) => {
    const current = new Date(selectedDate + 'T12:00:00');
    const newDate = new Date(current);
    
    if (direction === 'prev') {
      newDate.setDate(current.getDate() - 1);
    } else if (direction === 'next') {
      newDate.setDate(current.getDate() + 1);
    } else {
      // Pour "aujourd'hui", utiliser une nouvelle instance de Date
      newDate.setTime(new Date().getTime());
    }
    
    const formattedDate = getFormattedDate(newDate);
    setSelectedDate(formattedDate);
  };

  useEffect(() => {
    const db = getFirestore();
    
    const unsubscribeVisits = onSnapshot(doc(db, 'analytics', 'visits'), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        
        // Mettre à jour les visites journalières
        const dailyData = Object.entries(data.dailyVisits || {})
          .map(([date, count]) => ({
            time: date,
            visits: count
          }))
          .sort((a, b) => a.time.localeCompare(b.time))
          .slice(-7); // Garder seulement les 7 derniers jours
        
        // Mettre à jour les visites mensuelles
        const monthlyData = Object.entries(data.monthlyVisits || {})
          .map(([month, count]) => ({
            time: month,
            visits: count
          }))
          .sort((a, b) => a.time.localeCompare(b.time));
        
        // Mettre à jour les visites horaires pour la date sélectionnée
        const hourlyData = Array.from({ length: 24 }, (_, hour) => {
          // Essayer les deux formats d'heure possibles
          const hourWithZero = String(hour).padStart(2, '0'); // Format "07"
          const hourWithoutZero = String(hour); // Format "7"
          let visits = 0;

          // Vérifier les deux formats possibles
          if (data.hourlyVisits?.[selectedDate]?.[hourWithZero]) {
            visits = parseInt(data.hourlyVisits[selectedDate][hourWithZero]);
          } else if (data.hourlyVisits?.[selectedDate]?.[hourWithoutZero]) {
            visits = parseInt(data.hourlyVisits[selectedDate][hourWithoutZero]);
          }

          return {
            time: hour,
            visits: visits
          };
        });

        // Calculer la date précédente
        const previousDate = new Date(selectedDate + 'T12:00:00');
        previousDate.setDate(previousDate.getDate() - 1);
        const formattedPreviousDate = getFormattedDate(previousDate);

        // Récupérer les données horaires précédentes
        const previousHourlyData = Array.from({ length: 24 }, (_, hour) => {
          const hourWithZero = String(hour).padStart(2, '0');
          const hourWithoutZero = String(hour);
          let visits = 0;

          if (data.hourlyVisits?.[formattedPreviousDate]?.[hourWithZero]) {
            visits = parseInt(data.hourlyVisits[formattedPreviousDate][hourWithZero]);
          } else if (data.hourlyVisits?.[formattedPreviousDate]?.[hourWithoutZero]) {
            visits = parseInt(data.hourlyVisits[formattedPreviousDate][hourWithoutZero]);
          }

          return { time: hour, visits };
        });

        setDailyVisits(dailyData);
        setMonthlyVisits(monthlyData);
        setHourlyVisits(hourlyData);
        setPreviousHourlyVisits(previousHourlyData);
      }
    });

    return () => {
      unsubscribeVisits();
    };
  }, [selectedDate]);

  // Modifier useEffect pour les stats
  useEffect(() => {
    let stats;
    
    switch (chartType) {
      case 'hourly':
        stats = calculateStats(hourlyVisits, previousHourlyVisits);
        break;
        
      case 'daily':
        // Pour les visites journalières, comparer avec les 7 jours précédents
        const currentDays = dailyVisits.slice(-7);
        const previousDays = dailyVisits.slice(-14, -7);
        stats = calculateStats(currentDays, previousDays);
        break;
        
      case 'monthly':
        // Pour les visites mensuelles, comparer avec les mois précédents
        const currentMonths = monthlyVisits.slice(-3);
        const previousMonths = monthlyVisits.slice(-6, -3);
        stats = calculateStats(currentMonths, previousMonths);
        break;
        
      default:
        stats = { total: 0, average: 0, peak: 0, trends: { total: 0, average: 0, peak: 0 } };
    }
    
    setCurrentStats(stats);
  }, [chartType, hourlyVisits, previousHourlyVisits, dailyVisits, monthlyVisits]);

  // Fonction pour afficher la tendance
  const renderTrend = (trend) => {
    if (trend === 0) return null;
    return (
      <span className={`trend ${trend > 0 ? 'positive' : 'negative'}`}>
        <i className={`fas fa-${trend > 0 ? 'arrow-up' : 'arrow-down'}`}></i>
        {Math.abs(trend)}%
      </span>
    );
  };

  // Modifier les labels des statistiques en fonction du type de graphique
  const getStatsLabels = () => {
    switch (chartType) {
      case 'hourly':
        return {
          total: 'Total des visites',
          peak: 'Pic de visites',
          average: 'Moyenne par heure active'
        };
      case 'daily':
        return {
          total: 'Total sur 7 jours',
          peak: 'Pic journalier',
          average: 'Moyenne par jour'
        };
      case 'monthly':
        return {
          total: 'Total sur 3 mois',
          peak: 'Pic mensuel',
          average: 'Moyenne par mois'
        };
    }
  };

  return (
    <div className="visits-chart">
      <div className="chart-stats-header">
        <div className="chart-title-section">
          <h2>Statistiques des visites</h2>
        </div>

        <div className="controls-wrapper">
          <div className="view-selector">
            <button 
              className={`view-btn ${chartType === 'hourly' ? 'active' : ''}`}
              onClick={() => setChartType('hourly')}
            >
              <i className="fas fa-clock"></i>
              <span>Par heure</span>
            </button>
            <button 
              className={`view-btn ${chartType === 'daily' ? 'active' : ''}`}
              onClick={() => setChartType('daily')}
            >
              <i className="fas fa-calendar-day"></i>
              <span>Par jour</span>
            </button>
            <button 
              className={`view-btn ${chartType === 'monthly' ? 'active' : ''}`}
              onClick={() => setChartType('monthly')}
            >
              <i className="fas fa-calendar-alt"></i>
              <span>Par mois</span>
            </button>
          </div>

          <div className="date-controls">
            <div className={`date-navigation ${chartType === 'hourly' ? 'visible' : 'hidden'}`}>
              <button 
                className="nav-btn"
                onClick={() => changeDate('prev')}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button 
                className="today-btn"
                onClick={() => changeDate('today')}
              >
                Aujourd'hui
              </button>
              <button 
                className="nav-btn"
                onClick={() => changeDate('next')}
                disabled={selectedDate === new Date().toISOString().split('T')[0]}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
            <p className="subtitle">
              {chartType === 'hourly' ? formatDate(selectedDate) :
               chartType === 'daily' ? 'Visites des 7 derniers jours' :
               'Visites mensuelles'}
            </p>
          </div>
        </div>
      </div>

      {/* Ajouter la section des statistiques */}
      <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-chart-line"></i>
          </div>
          <div className="stat-info">
            <p className="stat-label">{getStatsLabels().total}</p>
            <div className="stat-value-container">
              <h3 className="stat-value">{currentStats.total}</h3>
              {renderTrend(currentStats.trends?.total)}
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-bolt"></i>
          </div>
          <div className="stat-info">
            <p className="stat-label">{getStatsLabels().peak}</p>
            <div className="stat-value-container">
              <h3 className="stat-value">{currentStats.peak}</h3>
              {renderTrend(currentStats.trends?.peak)}
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-calculator"></i>
          </div>
          <div className="stat-info">
            <p className="stat-label">{getStatsLabels().average}</p>
            <div className="stat-value-container">
              <h3 className="stat-value">{currentStats.average}</h3>
              {renderTrend(currentStats.trends?.average)}
            </div>
          </div>
        </div>
      </div>

      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={300}>
          {chartType === 'daily' ? (
            <BarChart
              data={dailyVisits}
              margin={{ top: 15, right: 25, left: 15, bottom: 15 }}
            >
              <defs>
                <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0.4}/>
                </linearGradient>
              </defs>

              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false} 
                stroke="var(--color-border)"
              />

              <XAxis 
                dataKey="time"
                stroke="var(--color-text)"
                tick={{ fill: 'var(--color-text)' }}
                tickFormatter={formatDate}
                angle={-45}
                textAnchor="end"
                height={80}
                axisLine={false}
                tickLine={false}
              />

              <YAxis 
                stroke="var(--color-text)"
                tick={{ fill: 'var(--color-text)' }}
                axisLine={false}
                tickLine={false}
              />

              <Tooltip content={<CustomTooltip chartType={chartType} />} />

              <Bar
                dataKey="visits"
                fill="url(#colorVisits)"
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              />
            </BarChart>
          ) : (
            <AreaChart 
              data={chartType === 'monthly' ? monthlyVisits : hourlyVisits}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
                </linearGradient>
              </defs>

              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false}
                stroke="var(--color-border)"
              />

              <XAxis 
                dataKey="time"
                stroke="var(--color-text)"
                tick={{ fill: 'var(--color-text)' }}
                tickFormatter={chartType === 'monthly' ? formatMonth : formatHour}
                axisLine={false}
                tickLine={false}
                interval={chartType === 'monthly' ? 0 : 2}
              />

              <YAxis 
                stroke="var(--color-text)"
                tick={{ fill: 'var(--color-text)' }}
                axisLine={false}
                tickLine={false}
              />

              <Tooltip content={<CustomTooltip chartType={chartType} />} />

              <Area
                type="monotone"
                dataKey="visits"
                stroke="var(--color-primary)"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorVisits)"
                dot={chartType === 'monthly'}
                activeDot={{ r: 6, fill: "var(--color-background-light)", stroke: "var(--color-primary)", strokeWidth: 2 }}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default VisitsChart; 